<template>
  <section
    ref="wrapRef"
    class="remuneration-stack-chart rounded-2xl border border-gray-modern-100 bg-white p-6 shadow-sm"
    :aria-label="title"
  >
    <div class="flex min-w-0 items-start justify-between gap-3">
      <div class="min-w-0 flex-1 flex flex-col gap-1">
        <h3 class="remuneration-stack-chart__title">{{ title }}</h3>
        <p class="remuneration-stack-chart__subtitle">
          {{ subtitle }}
        </p>
      </div>
      <ChartImageDownloadButton
        v-if="downloadable"
        :disabled="!canDownload"
        :busy="isDownloading"
        @click="downloadImage"
      />
    </div>

    <div class="mt-6 flex flex-wrap items-center justify-between gap-x-4 gap-y-4">
      <div class="remuneration-stack-chart__toggle" role="radiogroup" aria-label="Worker group">
        <button
          v-for="option in groupOptions"
          :key="option"
          type="button"
          role="radio"
          :aria-checked="selectedGroup === option"
          class="remuneration-stack-chart__toggle-btn"
          :class="selectedGroup === option ? 'remuneration-stack-chart__toggle-btn--active' : ''"
          @click="selectedGroup = option"
        >
          {{ option }}
        </button>
      </div>

      <ul class="remuneration-stack-chart__legend m-0 flex list-none flex-wrap items-center gap-x-6 gap-y-2 p-0">
        <li v-for="item in legendItems" :key="item.label" class="remuneration-stack-chart__legend-item">
          <span class="remuneration-stack-chart__legend-dot" :style="{ background: item.color }" />
          {{ item.label }}
        </li>
      </ul>
    </div>

    <div v-if="visibleRows.length" class="mt-6 flex flex-col gap-4">
      <div
        v-for="row in visibleRows"
        :key="`${row.facility}-${row.country}-${row.year}`"
        class="remuneration-stack-chart__row"
      >
        <div class="remuneration-stack-chart__row-label">
          <span>{{ row.facility }}</span>
          <span>{{ row.country }}{{ row.year ? ` • ${row.year}` : "" }}</span>
        </div>

        <div class="remuneration-stack-chart__bar" aria-hidden="true">
          <span
            class="remuneration-stack-chart__segment remuneration-stack-chart__segment--base"
            :style="{ flexBasis: `${row.values.baseWage}%` }"
          />
          <span
            class="remuneration-stack-chart__segment remuneration-stack-chart__segment--bonuses"
            :style="{ flexBasis: `${row.values.bonuses}%` }"
          />
          <span
            class="remuneration-stack-chart__segment remuneration-stack-chart__segment--in-kind"
            :style="{ flexBasis: `${row.values.inKind}%` }"
          />
        </div>
      </div>
    </div>
    <p v-else class="remuneration-stack-chart__empty mt-6">
      No remuneration breakdown data available for the selected filters.
    </p>
  </section>
</template>

<script setup>
import { computed, ref } from "vue";
import ChartImageDownloadButton from "./ChartImageDownloadButton.vue";
import { useChartImageDownload } from "../utils/useChartImageDownload";

const props = defineProps({
  title: {
    type: String,
    default: "Composition of total remuneration",
  },
  subtitle: {
    type: String,
    default: "Breakdown of pay components per facility",
  },
  rowsJson: {
    type: String,
    default: "",
  },
  downloadable: { type: Boolean, default: false },
  downloadContext: { type: String, default: "" },
});

const wrapRef = ref(null);

const selectedGroup = ref("All workers");
const groupOptions = ["All workers", "Women", "Men"];
const groupKeyByLabel = {
  "All workers": "all",
  Women: "women",
  Men: "men",
};

const legendItems = [
  { label: "Base wage", color: "var(--Brand-500, #347774)" },
  { label: "Bonuses", color: "var(--Brand-200, #81CCCA)" },
  { label: "In-kind benefits", color: "var(--IDH-Sun, #EDC042)" },
];

const parsedRows = computed(() => {
  if (typeof props.rowsJson !== "string" || !props.rowsJson.trim()) return [];

  try {
    const data = JSON.parse(props.rowsJson);
    return Array.isArray(data) ? data : [];
  } catch {
    return [];
  }
});

function componentValue(values, key) {
  const value = Number(values?.[key]);
  return Number.isFinite(value) && value > 0 ? value : 0;
}

const visibleRows = computed(() => {
  const groupKey = groupKeyByLabel[selectedGroup.value] || "all";
  return parsedRows.value
    .map((row) => {
      const values = row[groupKey];
      if (!values) return null;

      return {
        facility: row.facility,
        country: row.country,
        year: row.year,
        values: {
          baseWage: componentValue(values, "baseWage"),
          bonuses: componentValue(values, "bonuses"),
          inKind: componentValue(values, "inKind"),
        },
      };
    })
    .filter(Boolean);
});

const hasChartData = computed(() => visibleRows.value.length > 0);

const { downloadImage, isDownloading, canDownload } = useChartImageDownload({
  getRootElement: () => wrapRef.value,
  title: () => props.title,
  downloadContext: () => props.downloadContext,
  hasData: hasChartData,
});
</script>

<style scoped>
.remuneration-stack-chart__title {
  margin: 0;
  color: #111827;
  font-family: "Open Sans", sans-serif;
  font-size: 14px;
  font-style: normal;
  font-weight: 700;
  line-height: normal;
}

.remuneration-stack-chart__subtitle {
  margin: 0;
  color: #9ca3af;
  font-family: "Open Sans", sans-serif;
  font-size: 12px;
  font-style: normal;
  font-weight: 400;
  line-height: normal;
}

.remuneration-stack-chart__toggle {
  display: inline-flex;
  align-items: stretch;
  overflow: hidden;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  background: #f9fafb;
}

.remuneration-stack-chart__toggle-btn {
  min-width: 86px;
  cursor: pointer;
  border: 0;
  border-right: 1px solid #e5e7eb;
  padding: 10px 14px;
  background: transparent;
  color: #6b7280;
  font-family: "Open Sans", sans-serif;
  font-size: 14px;
  font-style: normal;
  font-weight: 700;
  line-height: normal;
  transition:
    background 0.15s ease,
    color 0.15s ease,
    box-shadow 0.15s ease;
}

.remuneration-stack-chart__toggle-btn:last-child {
  border-right: 0;
}

.remuneration-stack-chart__toggle-btn--active {
  background: #fff;
  color: #374151;
  box-shadow: 0 1px 2px rgba(16, 24, 40, 0.06);
}

.remuneration-stack-chart__toggle-btn:focus-visible {
  outline: 2px solid #265957;
  outline-offset: -2px;
}

.remuneration-stack-chart__legend {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 24px 8px;
  list-style: none;
  margin: 0;
  padding: 0;
}

.remuneration-stack-chart__legend-item {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  color: #6b7280;
  font-family: "Open Sans", sans-serif;
  font-size: 12px;
  font-style: normal;
  font-weight: 400;
  line-height: normal;
  white-space: nowrap;
}

.remuneration-stack-chart__legend-dot {
  width: 10px;
  height: 10px;
  flex-shrink: 0;
  border-radius: 999px;
}

.remuneration-stack-chart__row {
  display: grid;
  grid-template-columns: minmax(120px, 170px) minmax(0, 1fr);
  align-items: center;
  gap: 20px;
}

.remuneration-stack-chart__row-label {
  min-width: 0;
  color: #6b7280;
  font-family: "Open Sans", sans-serif;
  font-size: 12px;
  font-style: normal;
  font-weight: 400;
  line-height: 1.25;
  text-align: right;
}

.remuneration-stack-chart__row-label span {
  display: block;
}

.remuneration-stack-chart__bar {
  display: flex;
  width: 100%;
  height: 36px;
  min-width: 0;
  overflow: hidden;
  border-radius: 6px;
  background: #f3f4f6;
}

.remuneration-stack-chart__segment {
  display: block;
  flex-grow: 0;
  flex-shrink: 0;
  min-width: 2px;
}

.remuneration-stack-chart__segment--base {
  background: var(--Brand-500, #347774);
}

.remuneration-stack-chart__segment--bonuses {
  background: var(--Brand-200, #81CCCA);
}

.remuneration-stack-chart__segment--in-kind {
  background: var(--IDH-Sun, #EDC042);
}

.remuneration-stack-chart__empty {
  margin-bottom: 0;
  color: #6b7280;
  font-family: "Open Sans", sans-serif;
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: normal;
}

@media (max-width: 640px) {
  .remuneration-stack-chart__row {
    grid-template-columns: 1fr;
    gap: 8px;
  }

  .remuneration-stack-chart__row-label {
    text-align: left;
  }

  .remuneration-stack-chart__bar {
    height: 30px;
  }
}
</style>
